import { Controller } from "@hotwired/stimulus"

// No requests on connect. Searching and paging share one cancellable request;
// the ordinary list is kept in memory so clearing never needs a round trip.
export default class extends Controller {
    static targets = ["status", "message", "retry"]
    static values = { url: String, activeId: Number }

    get list() { return this.element.querySelector(".rail-chats-list") }
    get more() { return this.element.querySelector("[data-action='rail-search#loadMore']") }
    get empty() { return this.element.querySelector("[data-sidebar-section-target='filterEmpty']") }

    disconnect() { this._cancel() }

    _cancel() {
        clearTimeout(this.timer)
        this.request?.abort()
        this.revision = (this.revision || 0) + 1
        if (this.more) {
            this.more.disabled = false
            this.more.textContent = "More chats"
        }
    }

    search(event) {
        this._cancel()
        this.query = event.detail.query
        if (!this.query) return this._restore()
        if (!this.original) {
            this.original = { nodes: [...this.list.childNodes], cursor: this.more.dataset.cursor, hidden: this.more.hidden }
        }
        this.list.replaceChildren()
        this.more.hidden = true
        this.empty.hidden = true
        this._status("Searching chats…")
        this.timer = setTimeout(() => this._fetch(false), 250)
    }

    reset() {
        this._cancel()
        this.query = ""
        this.element.querySelector("[data-sidebar-section-target='filterInput']").value = ""
        this._restore()
    }

    _restore() {
        if (this.original) {
            this.list.replaceChildren(...this.original.nodes)
            this.more.dataset.cursor = this.original.cursor
            this.more.hidden = this.original.hidden
            this.original = null
        }
        if (!this.list.querySelector(".rail-chat, .rail-chats-empty")) {
            const empty = document.createElement("li")
            empty.className = "rail-chats-empty"
            empty.textContent = "No chats yet"
            this.list.append(empty)
        }
        this.statusTarget.hidden = true
        this.empty.hidden = true
    }

    syncOriginal(event) {
        const { id, title, deleted } = event.detail
        const nodes = this.original?.nodes || [...this.list.childNodes]
        const kept = nodes.filter(node => {
            const row = node.dataset?.conversationId === id ? node : node.querySelector?.(`[data-conversation-id='${id}']`)
            if (!row) return true
            if (deleted) {
                row.remove()
                if (row === node) return false
                if (node.matches?.(".rail-chat-project-group") && !node.querySelector(".rail-chat")) { node.remove(); return false }
                return true
            }
            row.querySelector(".rail-chat-label").textContent = title
            row.querySelector(".rail-chat-link").title = title
            return true
        })
        if (this.original) this.original.nodes = kept
    }

    loadMore() {
        if (!this.more.disabled) this._fetch(true)
    }

    retry() { this._fetch(this.retryAppend) }

    _status(message, failed = false) {
        this.statusTarget.hidden = false
        this.messageTarget.textContent = message
        this.retryTarget.hidden = !failed
    }

    async _fetch(append) {
        const revision = this.revision || 0
        this.request = new AbortController()
        this.more.disabled = true
        this.more.textContent = "Loading chats…"
        this._status(append ? "Loading chats…" : "Searching chats…")
        try {
            const url = new URL(this.urlValue, window.location.origin)
            if (this.query) url.searchParams.set("q", this.query)
            if (append) url.searchParams.set("cursor", this.more.dataset.cursor)
            const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin", signal: this.request.signal })
            if (!response.ok) throw new Error("Unable to load chats")
            const data = await response.json()
            if (revision !== (this.revision || 0)) return
            const template = document.createElement("template")
            template.innerHTML = data.html
            if (!append) this.list.replaceChildren()
            template.content.querySelectorAll(".rail-chat").forEach(row => {
                if (this.list.querySelector(`[data-conversation-id='${row.dataset.conversationId}']`)) return
                if (Number(row.dataset.conversationId) === this.activeIdValue) row.classList.add("is-active")
                const group = row.closest(".rail-chat-project-group")
                if (!group) { this.list.append(row); return }
                let destination = this.list.querySelector(`.rail-chat-project-group[data-project-id='${group.dataset.projectId}']`)
                if (!destination) {
                    destination = group.cloneNode(true)
                    destination.querySelector("ul").replaceChildren()
                    this.list.append(destination)
                }
                destination.querySelector("ul").append(row)
            })
            this.more.dataset.cursor = data.cursor || ""
            this.more.hidden = !data.more
            this.empty.hidden = !this.query || !!this.list.querySelector(".rail-chat")
            this.statusTarget.hidden = true
        } catch (error) {
            if (error.name !== "AbortError" && revision === (this.revision || 0)) {
                this.retryAppend = append
                this._status("Could not load chats. ", true)
            }
        } finally {
            if (revision === (this.revision || 0)) {
                this.more.disabled = false
                this.more.textContent = "More chats"
            }
        }
    }
}
